import { Controller } from "@hotwired/stimulus"
import { projectRange, dollars, exactDollars } from "../lib/projection"

// The plan form's live state (U4, spec 2026-09-29; three steps 2026-10-21):
// the country question (R13), amount (field and slider), preset cards and
// free integer inputs per bag, a live "adds up to X%" indicator, and, where
// the page has them, the horizon slider and the could-become range (the
// Plan page, through plan_edit_controller) or the builder's read-back
// summary (step 3: amount, preset name, bag percentages and bars). Saving
// is held until the country is answered and the mix adds up to exactly
// 100. The projection maths mirrors app/models/projection.rb through the
// bands serialised on the element. Every optional target is guarded, so a
// page renders only the parts it has.
export default class extends Controller {
  static targets = ["amount", "amountSlider", "years", "yearsOut",
    "gold", "stocks", "crypto", "total", "note",
    "mid", "pct", "low", "high", "contributed", "putInBar", "growthBar",
    "goldBar", "stocksBar", "cryptoBar", "presetChip", "customChip", "custom", "save",
    "country", "countryNote", "countryHint", "countryNext", "presets", "customSummary", "restrictedRow", "unavailable", "share", "picker",
    "summaryAmount", "summaryName", "summaryBag", "summaryPct", "summaryBar", "summaryStocks"]
  static values = { bands: Object, years: Number, restore: Object }

  connect() {
    this.closed = this.countryAnswer() === "no"
    this.applyCountry()
    this.refresh()
  }

  // The country answer (AE8): "no" closes Gold and Stocks (fields at 0 and
  // disabled, presets and the stock picker hidden, the picker's fieldset
  // disabled so it posts nothing) and puts everything in Crypto; "yes"
  // opens them again and, if the mix was left all-Crypto, restores the
  // balanced preset. The server renders the same state for the saved answer.
  country() {
    this.applyCountry()
    this.refresh()
  }

  applyCountry() {
    const answer = this.countryAnswer()
    const closed = answer === "no"
    const reopened = this.closed && answer === "yes"
    this.closed = closed

    for (const bag of ["gold", "stocks"]) {
      const target = this[`has${bag[0].toUpperCase()}${bag.slice(1)}Target`] ? this[`${bag}Target`] : null
      if (!target) continue
      target.disabled = closed
      if (closed) target.value = 0
    }
    if (closed && this.hasCryptoTarget) this.cryptoTarget.value = 100
    if (reopened) {
      const split = this.split()
      if (split.gold + split.stocks === 0) this.preset({ params: this.restoreValue })
    }

    this.presetsTargets.forEach((target) => { target.hidden = closed })
    // Closed, the rows are the plan: the disclosure opens and loses its
    // summary, so the step shows Gold and Stocks dimmed and Crypto at 100
    // instead of an empty card (2026-10-21).
    this.customSummaryTargets.forEach((target) => { target.hidden = closed })
    if (closed) this.customTargets.forEach((block) => { block.open = true })
    this.restrictedRowTargets.forEach((target) => { target.classList.toggle("opacity-60", closed) })
    this.unavailableTargets.forEach((target) => { target.hidden = !closed })
    this.shareTargets.forEach((target) => { target.hidden = closed })
    this.countryNoteTargets.forEach((target) => { target.hidden = !closed })
    this.pickerTargets.forEach((target) => {
      target.hidden = closed
      target.querySelectorAll("fieldset").forEach((fieldset) => { fieldset.disabled = closed })
    })
  }

  // "yes", "no", or null while unanswered. A form without the question
  // (none today) counts as answered.
  countryAnswer() {
    if (!this.hasCountryTarget) return "yes"
    const checked = this.countryTargets.find((radio) => radio.checked)
    return checked ? checked.value : null
  }

  preset({ params }) {
    for (const bag of ["gold", "stocks", "crypto"]) {
      const target = this[`${bag}Target`]
      if (target && !target.disabled) target.value = params[bag]
    }
    this.refresh()
  }

  // The slider and the typed field are one control: whichever moved wins.
  amountFromSlider() {
    this.amountTarget.value = this.amountSliderTarget.value
    this.refresh()
  }

  amountFromField() {
    this.fitAmount()
    if (this.hasAmountSliderTarget) {
      const value = this.amount()
      this.amountSliderTarget.value = Math.min(Math.max(value, Number(this.amountSliderTarget.min)), Number(this.amountSliderTarget.max))
    }
    this.refresh()
  }

  // The typed field hugs its digits (field-sizing: content where supported,
  // this width elsewhere) so the dollar sign sits against the number.
  fitAmount() {
    this.amountTarget.style.width = `${Math.max(2, String(this.amountTarget.value).length) + 0.4}ch`
  }

  refresh() {
    this.fitAmount()
    const split = this.split()
    const sum = split.gold + split.stocks + split.crypto

    const answered = this.countryAnswer() !== null
    this.totalTargets.forEach((target) => { target.textContent = sum })
    this.noteTargets.forEach((target) => { target.hidden = sum === 100 })
    this.countryHintTargets.forEach((target) => { target.hidden = answered })
    // The country step's own Next (2026-10-21): it waits for the answer alone.
    this.countryNextTargets.forEach((target) => { target.disabled = !answered })
    this.saveTargets.forEach((target) => { target.disabled = sum !== 100 || !answered || this.noStocks })
    // Both the horizon figure and the "over N years" span in the projection.
    this.yearsOutTargets.forEach((target) => { target.textContent = this.yearsLabel() })
    if (this.hasAmountSliderTarget) this.amountSliderTarget.setAttribute("aria-valuetext", `${dollars(this.amount())} a month`)
    if (this.hasYearsTarget) this.yearsTarget.setAttribute("aria-valuetext", this.yearsLabel())

    this.updateBars(split, sum)
    this.updateProjection(split, sum)
    const preset = this.updatePresetChips(split)
    this.updateSummary(split, sum, preset)
  }

  // The chip whose preset the mix matches reads pressed; a "Custom" chip, if
  // the page has one, reads pressed when none does, and a "Set my own"
  // disclosure, if the page has one, opens when none does (R8). Params are
  // read under this controller's own identifier, so a subclass (the plan
  // page's plan-edit) finds its own data-plan-edit-*-param attributes.
  // Returns the matched preset's name (its data-*-name-param), or null.
  updatePresetChips(split) {
    const prefix = this.identifier.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())
    let matched = null
    this.presetChipTargets.forEach((chip) => {
      const active = ["gold", "stocks", "crypto"].every(
        (bag) => Number(chip.dataset[`${prefix}${bag[0].toUpperCase()}${bag.slice(1)}Param`]) === split[bag]
      )
      if (active) matched = chip.dataset[`${prefix}NameParam`] || chip.textContent.trim()
      chip.setAttribute("aria-pressed", active)
    })
    this.customChipTargets.forEach((chip) => chip.setAttribute("aria-pressed", !matched))
    // A closed country answer leaves every preset unmatched by design; the
    // disclosure is already held open by applyCountry then.
    if (!matched && !this.closed) this.customTargets.forEach((block) => { block.open = true })
    return matched
  }

  // The builder's read-back (KTD6): the amount, the preset name or "Your own
  // mix" ("Crypto only" when the country answer closed Gold and Stocks),
  // each bag's percent and bar, and the stocks sentence, which the stock-mix
  // controller writes and this hides for a one-bag plan.
  updateSummary(split, sum, preset) {
    this.summaryAmountTargets.forEach((target) => { target.textContent = exactDollars(this.amount()) })
    this.summaryNameTargets.forEach((target) => {
      target.textContent = this.closed ? "Crypto only" : (preset || "Your own mix")
    })
    this.summaryBagTargets.forEach((row) => { row.hidden = this.closed && row.dataset.bag !== "crypto" })
    this.summaryPctTargets.forEach((target) => { target.textContent = `${split[target.dataset.bag] || 0}%` })
    this.summaryBarTargets.forEach((bar) => {
      bar.style.width = sum > 0 ? `${((split[bar.dataset.bag] || 0) / sum) * 100}%` : "0%"
    })
    this.summaryStocksTargets.forEach((target) => { target.hidden = this.closed })
  }


  updateBars(split, sum) {
    for (const bag of ["gold", "stocks", "crypto"]) {
      const bar = this[`has${bag[0].toUpperCase()}${bag.slice(1)}BarTarget`] ? this[`${bag}BarTarget`] : null
      if (!bar) continue
      bar.style.width = sum > 0 ? `${(split[bag] / sum) * 100}%` : "0%"
    }
  }

  updateProjection(split, sum) {
    if (!this.hasLowTarget || sum === 0) return

    const amount = Math.max(this.amount(), 0)
    const months = this.years() * 12
    const { low, high, mid, contributed } = projectRange({
      amount, months, bands: this.bandsValue, split, denom: sum
    })
    if (this.hasContributedTarget) this.contributedTarget.textContent = dollars(contributed)

    this.lowTarget.textContent = dollars(low)
    this.highTarget.textContent = dollars(high)
    if (this.hasMidTarget) this.midTarget.textContent = dollars(mid)
    if (this.hasPctTarget) {
      const growth = contributed > 0 ? ((mid - contributed) / contributed) * 100 : 0
      this.pctTarget.textContent = growth >= 1 ? `+${Math.round(growth)}%` : ""
    }
    if (this.hasPutInBarTarget && mid > 0) {
      const putIn = Math.min(100, (contributed / mid) * 100)
      this.putInBarTarget.style.width = `${putIn}%`
      this.growthBarTarget.style.width = `${100 - putIn}%`
    }
  }

  // The Stocks basket has dollars but no stock ticked to buy with them. The
  // server refuses that save ("Pick at least one stock"); Save waits for the
  // tick instead of sending the investor to the refusal. A form without the
  // picker (a crypto-only account) never trips it.
  get noStocks() {
    const picker = this.element.querySelector('input[name="plan[stocks_assets][]"]')
    return Boolean(picker) && this.stocks().length === 0 && this.split().stocks > 0
  }

  stocks() {
    return [...this.element.querySelectorAll('input[name="plan[stocks_assets][]"]:checked')]
      .map((box) => box.value).sort()
  }

  split() {
    const read = (target) => {
      if (!target || target.disabled) return 0
      const value = Math.round(Number(target.value))
      return Number.isFinite(value) ? Math.min(Math.max(value, 0), 100) : 0
    }
    return {
      gold: read(this.hasGoldTarget ? this.goldTarget : null),
      stocks: read(this.hasStocksTarget ? this.stocksTarget : null),
      crypto: read(this.hasCryptoTarget ? this.cryptoTarget : null)
    }
  }

  amount() {
    const value = Number(this.amountTarget.value)
    return Number.isFinite(value) ? value : 0
  }

  years() {
    if (this.hasYearsTarget) {
      const value = Number(this.yearsTarget.value)
      if (Number.isFinite(value) && value > 0) return value
    }
    return this.yearsValue
  }

  yearsLabel() {
    const years = this.years()
    return `${years} ${years === 1 ? "year" : "years"}`
  }
}
